import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import client from "@/lib/api";
import Loader from "@/components/Loader";
import usePageTitle from "@/hooks/usePageTitle";
import { COMPANIES } from "@/constants/testIds";

const SECTOR_ICONS = {
  "Strategy": "fa-chess-knight", "Communications": "fa-chess-knight",
  "Digital News": "fa-bolt", "Media": "fa-bolt",
  "AI": "fa-microchip", "Think Tank": "fa-microchip", "Policy": "fa-microchip",
  "Hearing": "fa-headphones", "Healthcare": "fa-headphones", "Audio": "fa-headphones",
  "Payments": "fa-indian-rupee-sign", "Fintech": "fa-indian-rupee-sign",
  "Live Entertainment": "fa-microphone", "Comedy": "fa-microphone",
  "Sports IP": "fa-baseball-bat-ball",
  "F&B": "fa-utensils", "Casual Dining": "fa-utensils",
};
function sectorIcon(sector) {
  if (!sector) return "fa-briefcase";
  for (const k of Object.keys(SECTOR_ICONS)) {
    if (sector.includes(k)) return SECTOR_ICONS[k];
  }
  return "fa-briefcase";
}

export function Companies() {
  const [items, setItems] = useState(null);
  usePageTitle("Companies & Investments", "A portfolio across strategy, digital media, AI policy, payments, audio tech, live entertainment and F&B — founded or backed by Nitin Narang.");

  useEffect(() => {
    client.get("/companies").then(r => setItems(r.data.items));
  }, []);

  if (!items) return <Loader label="Loading companies" />;

  return (
    <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
      <div className="mb-16">
        <div className="eyebrow mb-4">Companies & Investments</div>
        <h1 className="serif-title text-5xl lg:text-6xl text-[#0a1628] mb-4">A portfolio across strategy, media, audio and payments.</h1>
        <p className="text-[#4a4a4a] text-[17px] leading-relaxed max-w-3xl">
          Founder & CEO of <em>The Ideaz Factory</em>, Co-Founder of <em>Jist News</em>, Founder of <em>Future Shift Labs</em>, owner of <em>The Laugh Club</em>, producer of the <em>Comedians Cricket League</em>, Founder of <em>Khabar Gaon</em> (via <em>TIF Multimedia</em>, with technology by <em>TIF Technologies</em>), and associated with <em>HearClear India</em>, <em>InstaPe</em> and <em>Khaao Aur Khaane Do</em>.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6" data-testid={COMPANIES.list}>
        {items.map(c => (
          <Link key={c.slug} to={`/companies/${c.slug}`}
            data-testid={COMPANIES.item(c.slug)}
            className="border hairline bg-white p-10 card-hover group">
            <div className="flex items-start justify-between mb-8">
              <div className="flex-1">
                <div className="eyebrow mb-2">{c.sector}</div>
                <h2 className="font-display text-3xl text-[#0a1628]">{c.name}</h2>
                <div className="text-[13px] text-[#6b6b6b] mt-1">{c.role}{c.founded && c.founded !== "—" ? ` · est. ${c.founded}` : ""}</div>
              </div>
              {c.logo ? (
                <img src={c.logo} alt={c.name} className="h-12 max-w-[180px] object-contain" onError={(e) => {e.currentTarget.style.display='none'}}/>
              ) : (
                <div className="w-14 h-14 border hairline flex items-center justify-center text-[#b8975d] text-xl bg-[#faf8f3]">
                  <i className={`fa-solid ${sectorIcon(c.sector)}`}></i>
                </div>
              )}
            </div>
            <p className="text-[14px] text-[#4a4a4a] leading-relaxed mb-6">{c.tagline}</p>
            <div className="text-[12px] tracking-[0.18em] uppercase text-[#0a1628] flex items-center gap-2 group-hover:gap-3 transition-all">
              View Profile <i className="fa-solid fa-arrow-right text-[10px]"></i>
            </div>
          </Link>
        ))}
      </div>
    </main>
  );
}

export function CompanyDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  usePageTitle(c?.name ? `${c.name}` : "Company", c?.tagline);

  useEffect(() => {
    client.get(`/companies/${slug}`).then(r => setC(r.data)).catch(() => setErr("Company not found"));
  }, [slug]);

  if (err) return <main className="max-w-[1400px] mx-auto px-6 py-32 text-center"><h1 className="serif-title text-3xl">{err}</h1><Link to="/companies" className="link-underline mt-4 inline-block">← Back to Companies</Link></main>;
  if (!c) return <Loader label="Loading company" />;

  return (
    <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
      <Link to="/companies" className="link-underline text-[12px] tracking-[0.18em] uppercase mb-8 inline-block">← All Companies</Link>

      <div className="grid lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-8">
          <div className="eyebrow mb-4">{c.sector}{c.founded && c.founded !== "—" && ` · Founded ${c.founded}`}</div>
          <div className="flex items-start gap-6 mb-6">
            {c.logo ? (
              <img src={c.logo} alt={c.name} className="h-16 max-w-[260px] object-contain" onError={(e) => {e.currentTarget.style.display='none'}}/>
            ) : (
              <div className="w-16 h-16 border hairline flex items-center justify-center text-[#b8975d] text-2xl bg-[#faf8f3]">
                <i className={`fa-solid ${sectorIcon(c.sector)}`}></i>
              </div>
            )}
            <h1 className="serif-title text-5xl lg:text-6xl text-[#0a1628]">{c.name}</h1>
          </div>
          <div className="text-[#b8975d] eyebrow mb-6">{c.role}</div>
          <p className="text-[17px] text-[#2c2c2c] leading-relaxed max-w-3xl mb-6">{c.tagline}</p>
        </div>

        <aside className="lg:col-span-4 border hairline p-7 bg-white space-y-5 self-start">
          <div className="eyebrow">At a glance</div>
          <dl className="space-y-3 text-[14px]">
            <div className="flex justify-between border-b hairline pb-2"><dt className="text-[#6b6b6b]">Role</dt><dd className="text-[#0a1628] text-right">{c.role}</dd></div>
            <div className="flex justify-between border-b hairline pb-2"><dt className="text-[#6b6b6b]">Sector</dt><dd className="text-[#0a1628] text-right">{c.sector}</dd></div>
            <div className="flex justify-between border-b hairline pb-2"><dt className="text-[#6b6b6b]">Founded</dt><dd className="text-[#0a1628] text-right">{c.founded || "—"}</dd></div>
          </dl>
          <div className="flex flex-wrap gap-2 pt-3">
            {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="btn btn-ghost text-[11px] py-2 px-3">Website</a>}
            {c.links?.facebook && <a href={c.links.facebook} target="_blank" rel="noreferrer" className="btn btn-ghost text-[11px] py-2 px-3"><i className="fa-brands fa-facebook-f"></i></a>}
            {c.links?.instagram && <a href={c.links.instagram} target="_blank" rel="noreferrer" className="btn btn-ghost text-[11px] py-2 px-3"><i className="fa-brands fa-instagram"></i></a>}
            {c.links?.twitter && <a href={c.links.twitter} target="_blank" rel="noreferrer" className="btn btn-ghost text-[11px] py-2 px-3"><i className="fa-brands fa-x-twitter"></i></a>}
          </div>
        </aside>
      </div>

      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 prose-body">
          <div className="eyebrow mb-3">About</div>
          <p>{c.description}</p>
        </div>
        <div className="lg:col-span-4">
          <div className="eyebrow mb-4">Key milestones</div>
          <ul className="space-y-4">
            {c.milestones?.map((m, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-1.5 h-1.5 bg-[#b8975d] mt-2 flex-shrink-0"></span>
                <span className="text-[14px] text-[#2c2c2c] leading-relaxed">{m}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* PSIV — Problem / Solution / Impact / Vision */}
      {c.psiv && (
        <section className="mt-20">
          <div className="eyebrow mb-4">The story · in four frames</div>
          <h2 className="serif-title text-3xl lg:text-4xl text-[#0a1628] mb-10">
            Problem, solution, impact, <span className="italic text-[#b8975d]">vision.</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { k: "Problem", body: c.psiv.problem, icon: "fa-circle-exclamation" },
              { k: "Solution", body: c.psiv.solution, icon: "fa-lightbulb" },
              { k: "Impact", body: c.psiv.impact, icon: "fa-chart-line" },
              { k: "Vision", body: c.psiv.vision, icon: "fa-compass" },
            ].map((x, i) => x.body && (
              <div key={i} className={`border hairline p-7 ${i % 2 === 0 ? "bg-white" : "bg-[#faf8f3]"}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 border hairline flex items-center justify-center text-[#b8975d] bg-[#faf8f3]">
                    <i className={`fa-solid ${x.icon} text-sm`}></i>
                  </div>
                  <div className="eyebrow text-[#b8975d]">{x.k}</div>
                </div>
                <p className="text-[15px] text-[#2c2c2c] leading-[1.8]">{x.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
